<template>
  <div class="public-page" v-loading="loading">
    <div class="card" v-if="scale">
      <!-- 封面 -->
      <div v-if="!started" class="cover-wrapper">
        <h1 class="cover-title">{{ scale.title }}</h1>
        <div class="cover-content">
          <div class="cover-section" v-if="scale.description">
            <div class="section-label">简介</div>
            <div class="section-text">{{ scale.description }}</div>
          </div>
          <div class="cover-section" v-if="scale.instructions">
            <div class="section-label">指导语</div>
            <div class="section-text">{{ scale.instructions }}</div>
          </div>
        </div>
        <div class="cover-action">
          <el-button type="primary" size="medium" @click="startAssessment">开始测评</el-button>
        </div>
      </div>

      <!-- 测评过程 -->
      <div v-else class="assessment-wrapper">
        <div class="topbar">
          <div class="scale-title">{{ scale.title }}</div>
          <div class="progress-meta">
            <div class="meta">{{ currentIndex + 1 }} / {{ questions.length }}</div>
            <div class="meta">{{ progressPercent }}%</div>
          </div>
        </div>

        <el-progress :percentage="progressPercent" :show-text="false" :stroke-width="6"></el-progress>

        <div class="question-area" v-if="currentQuestion">
          <div class="q-title">{{ currentIndex + 1 }}. {{ currentQuestion.content }}</div>

          <div v-if="currentQuestion.widget_type === 'radio'" class="options">
            <div v-if="!currentQuestion.options || currentQuestion.options.length === 0" class="no-options">
              当前题目未配置任何选项，请联系管理员完善量表配置。
            </div>
            <button
              v-else
              v-for="opt in currentQuestion.options"
              :key="opt.id"
              class="option-btn"
              :class="{ active: isSelected(currentQuestion.id, opt.id) }"
              @click="selectOption(currentQuestion.id, opt.id)"
            >
              <span class="label">{{ opt.text }}</span>
              <span class="check" v-if="isSelected(currentQuestion.id, opt.id)">✓</span>
            </button>
          </div>

          <div v-else class="text-area">
            <el-input
              type="textarea"
              :rows="4"
              placeholder="请输入你的回答…"
              v-model="textAnswers[currentQuestion.id]"
            ></el-input>
          </div>
        </div>

        <div class="footer">
          <el-button :disabled="currentIndex === 0" @click="prev">上一题</el-button>
          <el-button type="primary" @click="nextOrSubmit" :loading="submitting">
            {{ currentIndex === questions.length - 1 ? '提交' : '下一题' }}
          </el-button>
        </div>
      </div>
    </div>

    <div class="empty" v-else>
      <div class="empty-title">未找到测评</div>
      <div class="empty-sub">请确认链接是否正确</div>
    </div>
  </div>
</template>

<script>
import { getPublicAssessment, submitPublicAssessment } from '@/api/assessments'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'PublicAssessment',
  props: ['uid'],
  data() {
    return {
      loading: false,
      submitting: false,
      started: false,
      scale: null,
      questions: [],
      currentIndex: 0,
      optionAnswers: {},
      textAnswers: {},
      autoNextTimer: null,
      autoNextLock: false,
    }
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex] || null
    },
    progressPercent() {
      if (!this.questions.length) return 0
      const done = this.currentIndex + 1
      return Math.round((done / this.questions.length) * 100)
    },
  },
  watch: {
    optionAnswers: {
      handler() {
        this.saveProgress()
      },
      deep: true,
    },
    textAnswers: {
      handler() {
        this.saveProgress()
      },
      deep: true,
    },
    currentIndex() {
      this.saveProgress()
    },
    started() {
      this.saveProgress()
    },
  },
  created() {
    this.fetchScale()
  },
  methods: {
    saveProgress() {
      const uid = this.uid || this.$route.params.uid
      if (!uid) return
      const key = `psyscale_progress_${uid}`
      const data = {
        optionAnswers: this.optionAnswers,
        textAnswers: this.textAnswers,
        currentIndex: this.currentIndex,
        started: this.started,
      }
      localStorage.setItem(key, JSON.stringify(data))
    },
    loadProgress() {
      const uid = this.uid || this.$route.params.uid
      if (!uid) return
      const key = `psyscale_progress_${uid}`
      const saved = localStorage.getItem(key)
      if (saved) {
        try {
          const data = JSON.parse(saved)
          if (data.optionAnswers) this.optionAnswers = data.optionAnswers
          if (data.textAnswers) this.textAnswers = data.textAnswers
          if (typeof data.currentIndex === 'number') this.currentIndex = data.currentIndex
          if (typeof data.started === 'boolean') this.started = data.started

          if (this.started) {
            this.$message.success('已为您恢复上次的答题进度')
          }
        } catch (e) {
          console.error('Failed to load progress', e)
        }
      }
    },
    startAssessment() {
      this.started = true
    },
    async fetchScale() {
      this.loading = true
      try {
        const resp = await getPublicAssessment(this.$route.params.uid)
        this.scale = resp.data.scale
        this.questions = (this.scale && this.scale.questions) || []
        this.currentIndex = 0
        this.loadProgress()
        if (!this.questions.length) {
          this.$message.error('该量表暂无任何题目，无法开始测评')
        }
      } catch (e) {
        if (e && e.response && e.response.status === 410) {
          this.$router.replace(`/public/${this.$route.params.uid}/result`)
          return
        }
        notifyApiError(e, '获取测评信息失败')
        this.scale = null
        this.questions = []
      } finally {
        this.loading = false
      }
    },
    isSelected(questionId, optionId) {
      return this.optionAnswers[questionId] === optionId
    },
    selectOption(questionId, optionId) {
      this.$set(this.optionAnswers, questionId, optionId)
      this.scheduleAutoNext()
    },
    scheduleAutoNext() {
      if (this.autoNextLock) return
      if (this.currentIndex >= this.questions.length - 1) return

      if (this.autoNextTimer) window.clearTimeout(this.autoNextTimer)
      this.autoNextTimer = window.setTimeout(() => {
        if (this.autoNextLock) return
        this.autoNextLock = true
        try {
          if (!this.validateCurrent()) return
          this.currentIndex += 1
        } finally {
          window.setTimeout(() => {
            this.autoNextLock = false
          }, 150)
        }
      }, 200)
    },
    prev() {
      this.currentIndex = Math.max(0, this.currentIndex - 1)
    },
    validateCurrent() {
      const q = this.currentQuestion
      if (!q) return true
      if (q.widget_type === 'radio') {
        if (!q.options || q.options.length === 0) {
          this.$message.warning('该题未配置选项，无法作答')
          return false
        }
        if (!this.optionAnswers[q.id]) {
          this.$message.warning('请选择一个选项')
          return false
        }
      } else if (q.widget_type === 'text') {
        const v = this.textAnswers[q.id]
        if (!v || !String(v).trim()) {
          this.$message.warning('请填写文本答案')
          return false
        }
      }
      return true
    },
    async nextOrSubmit() {
      if (!this.validateCurrent()) return
      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex += 1
        return
      }
      await this.submit()
    },
    buildPayload() {
      return this.questions.map((q) => {
        if (q.widget_type === 'radio') {
          return { question_id: q.id, option_id: this.optionAnswers[q.id] }
        }
        return { question_id: q.id, text_value: this.textAnswers[q.id] || '' }
      })
    },
    async submit() {
      this.submitting = true
      try {
        await submitPublicAssessment(this.$route.params.uid, {
          answers: this.buildPayload(),
        })
        const uid = this.uid || this.$route.params.uid
        if (uid) {
          localStorage.removeItem(`psyscale_progress_${uid}`)
        }
        this.$router.replace(`/public/${this.$route.params.uid}/result`)
      } catch (e) {
        notifyApiError(e, '提交失败')
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.public-page {
  min-height: 100vh;
  display: flex;
  align-items: center; /* Center vertically */
  justify-content: center;
  background: var(--bg-body);
  padding: 28px 14px;
}
.card {
  width: min(920px, 100%);
  background: var(--bg-card);
  border-radius: 16px;
  padding: 18px 18px 14px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.08);
}
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}
.scale-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--text-primary);
}
.progress-meta {
  display: flex;
  gap: 14px;
  color: var(--text-secondary);
  font-size: 13px;
}
.question-area {
  margin-top: 18px;
  min-height: 360px;
}
.q-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 14px;
}
.options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.no-options {
  height: 54px;
  border-radius: 10px;
  border: 1px dashed var(--border-color);
  background: var(--bg-body);
  display: flex;
  align-items: center;
  padding: 0 14px;
  color: var(--text-secondary);
  font-size: 13px;
}
.option-btn {
  height: 54px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: var(--bg-card);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.option-btn:hover {
  border-color: #cbd5e1;
  transform: translateY(-1px);
}
.option-btn.active {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}
.option-btn.active .label {
  color: #fff;
}
.label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}
.check {
  font-size: 16px;
  font-weight: 900;
}
.footer {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
}
.empty {
  width: min(680px, 100%);
  background: var(--bg-card);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(17, 24, 39, 0.08);
  text-align: center;
}
.empty-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--text-primary);
}
.empty-sub {
  margin-top: 8px;
  color: var(--text-secondary);
}

/* Cover Styles */
.cover-wrapper {
  padding: 10px;
}
.cover-title {
  font-size: 24px;
  font-weight: 800;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 32px;
}
.cover-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 40px;
}
.cover-section {
  background: var(--bg-body);
  border-radius: 12px;
  padding: 20px;
}
.section-label {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.section-text {
  font-size: 15px;
  line-height: 1.6;
  color: var(--text-regular);
  white-space: pre-wrap;
}
.cover-action {
  display: flex;
  justify-content: center;
}
.cover-action .el-button {
  padding: 12px 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
}
</style>
